"use client";

import { useEffect, useId, useImperativeHandle, useRef, useState, type Ref, type ReactNode } from "react";
import { serviceInformation } from "./content";
import { PrivacyPolicyBody } from "./PrivacyPolicyBody";
import { GuidedSubmitButton } from "@/components/forms/GuidedSubmitButton";
import { X } from "@app-foundation/icons/lucide";
import { useReadToEnd } from "./useReadToEnd";
import { AgreementConfirmation } from "./AgreementConfirmation";

export type PrivacyConsentHandle = { requestConsent: (onAccepted: () => void) => void };

export function ScrollConsent({ agreed, onChange, title, documentLabel = title, children, separate = false, compact = false, confirmationText, confirmationSuffix, name, required, ref, triggerLabel, triggerClassName, hideTrigger = false, confirmLabel = "阅读完成并同意", onConfirm, blockedReason = "", confirmBlockedReason = "", confirmFocusSelector = 'input[type="checkbox"]:not(:checked):not(:disabled)', disabled = false, readOnly = false }: {
  agreed: boolean;
  onChange: (agreed: boolean) => void;
  title: string;
  documentLabel?: string;
  children: ReactNode;
  separate?: boolean;
  compact?: boolean;
  confirmationText?: string;
  confirmationSuffix?: string;
  name?: string;
  required?: boolean;
  triggerLabel?: string;
  triggerClassName?: string;
  hideTrigger?: boolean;
  confirmLabel?: string;
  onConfirm?: () => Promise<void>;
  blockedReason?: string;
  confirmBlockedReason?: string;
  confirmFocusSelector?: string;
  disabled?: boolean;
  readOnly?: boolean;
  ref?: Ref<PrivacyConsentHandle>;
}) {
  const [open, setOpen] = useState(false);
  const { body, checkEnd, reachedEnd } = useReadToEnd(open, title);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const pendingAction = useRef<(() => void) | null>(null);
  const titleId = useId();

  useImperativeHandle(ref, () => ({
    requestConsent(onAccepted) {
      if (disabled || blockedReason || submitting.current) return;
      pendingAction.current = onAccepted;
      setError("");
      setOpen(true);
    }
  }), [disabled, blockedReason]);

  useEffect(() => {
    if (!open) return;
    const element = dialog.current;
    const content = body.current;
    if (!element || !content) return;
    element.showModal();
    content.focus({ preventScroll: true });
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      element.close();
    };
  }, [open, body]);

  const openPolicy = () => { if (disabled || blockedReason || submitting.current) return; pendingAction.current = null; setError(""); setOpen(true); };
  const closePolicy = () => { if (submitting.current) return; pendingAction.current = null; setOpen(false); };
  async function confirm() {
    if (!reachedEnd || disabled || readOnly || blockedReason || confirmBlockedReason || submitting.current) return;
    submitting.current = true; setBusy(true); setError("");
    try {
      if (onConfirm) await onConfirm();
      const continueAction = pendingAction.current;
      pendingAction.current = null;
      onChange(true);
      setOpen(false);
      continueAction?.();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "确认未保存，请重试。"); }
    finally { submitting.current = false; setBusy(false); }
  }

  return <>
    {!hideTrigger && (triggerLabel ? <GuidedSubmitButton type="button" className={triggerClassName} disabled={disabled || busy} reason={blockedReason} onClick={openPolicy}>{triggerLabel}</GuidedSubmitButton> : <AgreementConfirmation
      compact={compact} checked={agreed} name={name} required={required} disabled={disabled || busy || readOnly} readDisabled={disabled || busy}
      onChange={() => { if (agreed) onChange(false); else openPolicy(); }} onOpen={openPolicy}
      documentLabel={documentLabel} statement={confirmationText ?? (separate ? "本人确认阅读并理解文件内容，并单独同意" : "本人确认阅读并理解文件内容，并自愿同意")} suffix={confirmationSuffix} />)}
    {open && <dialog ref={dialog} aria-labelledby={titleId} onCancel={event => { event.preventDefault(); event.stopPropagation(); closePolicy(); }} onClose={event => { event.stopPropagation(); if (!event.currentTarget.open) closePolicy(); }}
      className="fixed inset-0 m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-xl overflow-hidden rounded-3xl border border-[var(--color-line)] bg-[var(--color-raised)] p-0 text-[var(--color-paper)] shadow-2xl backdrop:bg-black/50">
      <div className="flex max-h-[90dvh] flex-col">
        <header className="flex shrink-0 items-center justify-between gap-4 border-b border-[var(--color-line)] px-6 py-5">
          <h2 id={titleId} className="text-xl font-semibold">{title}</h2>
          <button type="button" aria-label={`关闭${documentLabel}`} disabled={busy} className="inline-flex min-h-11 min-w-11 items-center justify-center text-[var(--color-muted)]" onClick={closePolicy}><X size={20} aria-hidden="true" /></button>
        </header>
        <div ref={body} tabIndex={0} role="region" aria-label={`${documentLabel}全文`} onScroll={checkEnd}
          className="min-h-0 max-h-[55dvh] overflow-y-auto overscroll-contain px-6 py-5 outline-offset-[-2px]">
          <fieldset disabled={busy} className="min-w-0">{children}</fieldset>
        </div>
        {!readOnly && <footer className="shrink-0 border-t border-[var(--color-line)] px-6 py-4">
          {error && <p role="alert" className="rg-error mb-3">{error}</p>}
          <GuidedSubmitButton type="button" disabled={busy || disabled} reason={!reachedEnd ? "请滚动至底部，阅读完整份文件。" : blockedReason || confirmBlockedReason}
            focusSelector={reachedEnd ? confirmFocusSelector : undefined}
            onBlocked={() => { if (!reachedEnd) body.current?.focus({ preventScroll: true }); }}
            className="btn-silver w-full rounded-full px-5 py-3 text-sm disabled:opacity-40"
            onClick={() => void confirm()}>{busy ? "正在保存…" : confirmLabel}</GuidedSubmitButton>
        </footer>}
      </div>
    </dialog>}
  </>;
}

export function PrivacyConsent({ purpose, ...props }: {
  agreed: boolean;
  onChange: (agreed: boolean) => void;
  purpose: string;
  name?: string;
  required?: boolean;
  ref?: Ref<PrivacyConsentHandle>;
}) {
  return <ScrollConsent {...props} title={serviceInformation.privacy.title}>
    {purpose && <p className="mb-5 text-sm leading-7">{purpose}</p>}
    <PrivacyPolicyBody />
  </ScrollConsent>;
}

export function PrivacyFormConsent() {
  const [agreed, setAgreed] = useState(false);
  return <PrivacyConsent agreed={agreed} onChange={setAgreed} purpose="" name="privacyConfirmed" required />;
}
